import * as React from 'react';
import { useState, useRef } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { BaseDialog } from '../dialog/BaseDialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import styled from '@mui/material/styles/styled';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { StyledTextField } from '../StyledTextField';
import { StyledFormControl } from '../StyledFormControl';
import { Lecture, BulkRegisterResult } from '../../hooks/useUserData';
import { parseBulkUserCsv, markExistingEmails, CsvParseResult, BulkUserInput } from '../../utils/csvUtils';
import { colors } from '../../theme/colors';

// Custom styled dialog for user add with different dimensions
export const UserAddDialog = styled(BaseDialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '500px',
    maxHeight: '550px',
  },
}));

// 一括登録ダイアログ用(プレビューテーブルを表示するため少し広め)
export const BulkUserDialog = styled(BaseDialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '92%',
    maxWidth: '760px',
    maxHeight: '640px',
  },
}));


// ユーザー追加ダイアログ
interface UserAddDialogProps {
  open: boolean;
  onClose: () => void;
  lectures: Lecture[];
  onAddUser: (userName: string, email: string, password: string, lectureIds: string[]) => void;
}

interface FormErrors {
  userName: string;
  email: string;
  password: string;
  lectures: string;
}

export const AddUserDialog: React.FC<UserAddDialogProps> = ({
  open,
  onClose,
  lectures,
  onAddUser,
}) => {
  // フォーム状態
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedLectureIds, setSelectedLectureIds] = useState<string[]>([]);
  
  // エラー状態
  const [errors, setErrors] = useState<FormErrors>({
    userName: '',
    email: '',
    password: '',
    lectures: '',
  });

  // フォームリセット
  const resetForm = () => {
    setUserName('');
    setEmail('');
    setPassword('');
    setSelectedLectureIds([]);
    setErrors({
      userName: '',
      email: '',
      password: '',
      lectures: '',
    });
  };

  // フォームクローズ処理
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // 入力検証
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      userName: '',
      email: '',
      password: '',
      lectures: '',
    };
    
    let isValid = true;

    if (!userName.trim()) {
      newErrors.userName = 'ユーザー名が入力されていません';
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = 'メールアドレスが入力されていません';
      isValid = false;
    }

    if (!password.trim()) {
      newErrors.password = 'パスワードが入力されていません';
      isValid = false;
    }

    if (selectedLectureIds.length === 0) {
      newErrors.lectures = '講座が選択されていません';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // 送信処理
  const handleSubmit = () => {
    if (validateForm()) {
      onAddUser(userName, email, password, selectedLectureIds);
    }
  };

  return (
    <UserAddDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="user-add-dialog"
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: 'white',
          fontWeight: 'bold',
          fontSize: '23px',
        }}
        id="user-add-dialog-title"
        align="center"
      >
        <PersonAddAltOutlinedIcon
          fontSize="large"
          sx={{ verticalAlign: 'middle', mr: 1 }}
        />
        受講者追加
      </DialogTitle>
      <IconButton
        aria-label="close-user-add-dialog"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'white',
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <StyledTextField
          label="ユーザー名"
          variant="standard"
          onChange={(e) => {
            setUserName(e.target.value);
            setErrors((prev) => ({ ...prev, userName: '' }));
          }}
          value={userName}
          sx={{ m: 1, minWidth: 250 }}
          required
          error={!!errors.userName}
          helperText={errors.userName}
        />
        <StyledTextField
          label="メールアドレス"
          type="email"
          variant="standard"
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors((prev) => ({ ...prev, email: '' }));
          }}
          value={email}
          sx={{ m: 1, minWidth: 250 }}
          required
          error={!!errors.email}
          helperText={errors.email}
        />
        <StyledTextField
          label="パスワード"
          type="password"
          variant="standard"
          onChange={(e) => {
            setPassword(e.target.value);
            setErrors((prev) => ({ ...prev, password: '' }));
          }}
          value={password}
          sx={{ m: 1, minWidth: 250 }}
          required
          error={!!errors.password}
          helperText={errors.password}
        />
        <StyledFormControl
          variant="standard"
          sx={{ m: 1, minWidth: 250 }}
          required
          error={!!errors.lectures}
        >
          <InputLabel>登録する講座</InputLabel>
          <Select
            multiple
            value={selectedLectureIds}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedLectureIds(typeof value === 'string' ? value.split(',') : value);
              setErrors((prev) => ({ ...prev, lectures: '' }));
            }}
            renderValue={(selected) => `${selected.length}個選択されました`}
          >
            {lectures.map((lecture) => (
              <MenuItem key={lecture.id} value={lecture.id}>
                <Checkbox checked={selectedLectureIds.indexOf(lecture.id) > -1} />
                <ListItemText primary={lecture.lectureName} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.lectures}</FormHelperText>
        </StyledFormControl>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            width: '50%',
            color: colors.primary,
            alignSelf: 'center',
            backgroundColor: 'white',
            borderRadius: '16px',
            border: `2px solid ${colors.primary}`,
            fontSize: '15px',
            '&:hover': {
              color: 'white',
              transform: 'scale(1.1)',
              backgroundColor: colors.primary,
            },
          }}
        >
          追加
        </Button>
      </DialogContent>
    </UserAddDialog>
  );
};


// 受講者一括登録(CSVインポート)ダイアログ
interface BulkAddUserDialogProps {
  open: boolean;
  onClose: () => void;
  lectures: Lecture[];
  // 一括登録 API を呼ぶ。成功/失敗内訳を返す(失敗通信時は null)。
  onBulkRegister: (
    users: BulkUserInput[],
    lectureIds: string[]
  ) => Promise<BulkRegisterResult | null>;
  // 既存DB重複の事前チェック。email配列のうち既に登録済みのものを返す(失敗時 null)。
  onCheckExisting?: (emails: string[]) => Promise<string[] | null>;
  // 1 件以上登録できたときに呼ぶ(一覧の再取得用)。
  onCompleted?: () => void;
}

const primaryButtonSx = {
  color: colors.primary,
  backgroundColor: 'white',
  borderRadius: '16px',
  border: `2px solid ${colors.primary}`,
  fontSize: '15px',
  '&:hover': {
    color: 'white',
    transform: 'scale(1.05)',
    backgroundColor: colors.primary,
  },
};

export const BulkAddUserDialog: React.FC<BulkAddUserDialogProps> = ({
  open,
  onClose,
  lectures,
  onBulkRegister,
  onCheckExisting,
  onCompleted,
}) => {
  const [selectedLectureIds, setSelectedLectureIds] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkRegisterResult | null>(null);
  const [formError, setFormError] = useState('');
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [existingCheckFailed, setExistingCheckFailed] = useState(false);
  const [existingCount, setExistingCount] = useState(0);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetAll = () => {
    setSelectedLectureIds([]);
    setFileName('');
    setParseResult(null);
    setSubmitting(false);
    setResult(null);
    setFormError('');
    setCheckingExisting(false);
    setExistingCheckFailed(false);
    setExistingCount(0);
    setProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    // 登録処理中は閉じさせない(中断・状況喪失を防ぐ)。完了後に閉じられる。
    if (submitting) return;
    resetAll();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setResult(null);
    setFormError('');
    const file = e.target.files?.[0];
    if (!file) {
      setFileName('');
      setParseResult(null);
      return;
    }
    setFileName(file.name);
    setExistingCheckFailed(false);
    setExistingCount(0);
    const text = await file.text();
    const parsed = parseBulkUserCsv(text);
    setParseResult(parsed);

    // 既存DB重複を送信前に一括チェック(単一バッチ問い合わせ)。形式OKな行のみ照会。
    if (onCheckExisting && parsed.valid.length > 0) {
      setCheckingExisting(true);
      const existing = await onCheckExisting(parsed.valid.map((v) => v.email));
      setCheckingExisting(false);
      if (existing === null) {
        // 事前チェック失敗時はプレビュー反映をスキップ(最終判定はサーバ側に委ねる)
        setExistingCheckFailed(true);
      } else if (existing.length > 0) {
        setExistingCount(existing.length);
        setParseResult(markExistingEmails(parsed, existing));
      }
    }
  };

  const handleSubmit = async () => {
    setFormError('');
    if (selectedLectureIds.length === 0) {
      setFormError('講座が選択されていません');
      return;
    }
    if (!parseResult || parseResult.valid.length === 0) {
      setFormError('登録できる有効な行がありません');
      return;
    }
    // 大量データはチャンク分割して順次送信する。
    //  - 1リクエストが短くなり Cloud Run のタイムアウトを避けられる
    //  - チャンクごとに commit されるので途中まで確実に保存される
    //  - 進捗を表示でき、処理中はモーダルを閉じさせない(状況喪失を防ぐ)
    const CHUNK_SIZE = 100;
    const all = parseResult.valid;
    setSubmitting(true);
    setProgress({ done: 0, total: all.length });

    const created: BulkRegisterResult['created'] = [];
    const failed: BulkRegisterResult['failed'] = [];
    let commError = false;

    for (let i = 0; i < all.length; i += CHUNK_SIZE) {
      const chunk = all.slice(i, i + CHUNK_SIZE);
      const res = await onBulkRegister(chunk, selectedLectureIds);
      if (res) {
        created.push(...(res.created ?? []));
        failed.push(...(res.failed ?? []));
      } else {
        // このチャンクは通信失敗。未登録の可能性として記録し、後続チャンクは継続する。
        commError = true;
        failed.push(
          ...chunk.map((c) => ({ row: 0, email: c.email, reason: '通信エラー(このチャンクは未登録の可能性)' }))
        );
      }
      setProgress({ done: Math.min(i + CHUNK_SIZE, all.length), total: all.length });
    }

    setSubmitting(false);
    setResult({
      createdCount: created.length,
      failedCount: failed.length,
      created,
      failed,
      message: `${created.length} 件登録、${failed.length} 件失敗`,
    });
    if (created.length > 0 && onCompleted) onCompleted();
    if (commError) {
      setFormError('一部のチャンクで通信エラーが発生しました。結果をご確認のうえ、未登録分だけ再アップロードしてください。');
    }
  };

  const errorRows = parseResult?.rows.filter((r) => r.error) ?? [];
  const validCount = parseResult?.valid.length ?? 0;

  return (
    <BulkUserDialog open={open} onClose={handleClose} aria-labelledby="bulk-user-add-dialog" disableEscapeKeyDown={submitting}>
      <DialogTitle
        sx={{ backgroundColor: colors.primary, color: 'white', fontWeight: 'bold', fontSize: '21px' }}
        id="bulk-user-add-dialog-title"
        align="center"
      >
        <PersonAddAltOutlinedIcon fontSize="large" sx={{ verticalAlign: 'middle', mr: 1 }} />
        受講者一括登録(CSV)
      </DialogTitle>
      <IconButton
        aria-label="close-bulk-user-add-dialog"
        onClick={handleClose}
        disabled={submitting}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {result ? (
          // ===== 登録結果(画面遷移せずダイアログ内に表示) =====
          <Box sx={{ textAlign: 'center', py: 3, px: 1 }}>
            {result.failedCount === 0 ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />
            ) : (
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'warning.main' }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>
              {result.failedCount === 0 ? '登録が完了しました' : '登録が完了しました（一部失敗）'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 5, my: 2.5 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: colors.primary, lineHeight: 1.1 }}>
                  {result.createdCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">登録成功</Typography>
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 'bold', lineHeight: 1.1, color: result.failedCount ? 'error.main' : 'text.disabled' }}
                >
                  {result.failedCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">失敗</Typography>
              </Box>
            </Box>
            {result.failed.length > 0 && (
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1, textAlign: 'left' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  失敗した行:
                </Typography>
                {result.failed.map((f, i) => (
                  <Typography key={`${f.email}-${i}`} variant="body2" color="error">
                    {f.email}: {f.reason}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <>
            <StyledFormControl variant="standard" sx={{ m: 1, minWidth: 250 }} required>
              <InputLabel>登録する講座</InputLabel>
              <Select
                multiple
                value={selectedLectureIds}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedLectureIds(typeof value === 'string' ? value.split(',') : value);
                  setFormError('');
                }}
                renderValue={(selected) => `${selected.length}個選択されました`}
              >
                {lectures.map((lecture) => (
                  <MenuItem key={lecture.id} value={lecture.id}>
                    <Checkbox checked={selectedLectureIds.indexOf(lecture.id) > -1} />
                    <ListItemText primary={lecture.lectureName} />
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>

            <Box sx={{ m: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleFile}
              />
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                sx={{ color: colors.primary, borderColor: colors.primary, borderRadius: '16px' }}
              >
                CSVファイルを選択
              </Button>
              {fileName && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {fileName}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                形式: 各行「名前,メールアドレス,パスワード」(1行目のヘッダは任意)
              </Typography>
            </Box>

            {parseResult?.fileError && (
              <Typography color="error" variant="body2" sx={{ m: 1 }}>
                {parseResult.fileError}
              </Typography>
            )}
            {parseResult && !parseResult.fileError && (
              <Box sx={{ m: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  登録予定 {validCount} 件 / エラー {errorRows.length} 件
                  {existingCount > 0 && `（うち既存DB重複 ${existingCount} 件）`}
                  （CSV {parseResult.rows.length} 行）
                </Typography>
                {checkingExisting && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    既存登録との重複を確認中...
                  </Typography>
                )}
                {existingCheckFailed && (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                    既存重複の事前確認に失敗しました（送信時にサーバ側で判定されます）。
                  </Typography>
                )}
                {/* 登録予定の受講者一覧(名前・メールアドレス・パスワード)。エラー行は赤背景で理由を表示。 */}
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader aria-label="一括登録プレビュー">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: 40 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>名前</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>メールアドレス</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>パスワード</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>状態</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parseResult.rows.map((r) => (
                        <TableRow
                          key={r.row}
                          sx={r.error ? { backgroundColor: '#fdecea' } : undefined}
                        >
                          <TableCell>{r.row}</TableCell>
                          <TableCell>{r.name || '-'}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{r.email || '-'}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{r.password || '-'}</TableCell>
                          <TableCell sx={{ color: r.error ? 'error.main' : 'success.main', whiteSpace: 'nowrap' }}>
                            {r.error ?? '登録対象'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {formError && (
              <Typography color="error" variant="body2" sx={{ m: 1 }}>
                {formError}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      {/* アクションは固定フッターに置き、プレビューが長くてもボタンが見切れないようにする */}
      <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
        {submitting && (
          <Typography variant="caption" color="text.secondary" align="center">
            登録処理中です。完了するまでこのダイアログを閉じないでください。
          </Typography>
        )}
        {result ? (
          <Button variant="contained" onClick={handleClose} sx={{ width: '50%', ...primaryButtonSx }}>
            閉じる
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || checkingExisting || validCount === 0 || selectedLectureIds.length === 0}
            sx={{ width: '60%', ...primaryButtonSx }}
          >
            {submitting
              ? `登録中... ${progress ? `${progress.done}/${progress.total}` : ''}`
              : checkingExisting
              ? '確認中...'
              : `${validCount}件を登録`}
          </Button>
        )}
      </DialogActions>
    </BulkUserDialog>
  );
};